import React from 'react'
import { useAppSelector } from '../../../../store/hooks'
import { selectCrossClientClients } from '../../../../store/crossClient/crossClientResults'
import { selectSafeAuthenticatedUser } from '../../../../store/auth/authSlice'
import { Tooltip } from '../../../../components/Tooltip/Tooltip'
import { Button, type ButtonVariant } from '../../../../components/Button/Button'
import { type CrossClientQueryResultDisplay } from '../../../../models/CrossClient'
import { getUserDisplayName } from '../../../../apps/professional-app/fa/common/aifeatures/utils'
import { ActionsDropdownItem, ActionsToggle, CrossClientActionsDropdown } from '../../Actions/CrossClientActionsDropdown'
import { useCrmActions } from '../../Actions/useCrmActions'
import { useExportCrossClientResults } from '../../Results/hooks'
import classNames from 'classnames'

export interface CrossClientResultButtonsProps {
  result: CrossClientQueryResultDisplay
  variant?: ButtonVariant
  withText?: boolean
  className?: string
}

export const CrossClientResultButtons: React.FC<CrossClientResultButtonsProps> = ({ result, variant = 'borderless', withText = false, className }) => {
  const clients = useAppSelector(selectCrossClientClients)
  const currentUserId = useAppSelector(selectSafeAuthenticatedUser).id

  const client = clients[result.clientId]

  const disabled = !client || (client.owner?.ownerId && client.owner.ownerId !== currentUserId)

  const buttonClassName = classNames('p-2 cross-client-cta button-small border rounded-2', className)

  const crmActions = useCrmActions([result])
  const clientName = client ? getUserDisplayName(client) : undefined
  const { disabled: exportDisabled, exportCSV } = useExportCrossClientResults([result])

  const ariaLabel = clientName ? `Actions for ${clientName}` : 'Actions'

  if (disabled) {
    return (
      <Tooltip className='zocks-text-gray-500' placement='top' title='Only the contact owner can run actions on this client.'>
        <Button variant={variant} className={buttonClassName} aria-label={ariaLabel} disabled>
          <i className='bi bi-three-dots fs-6' />
          {withText ? 'Actions' : null}
        </Button>
      </Tooltip>
    )
  }

  return (
    <CrossClientActionsDropdown
      count={1}
      singleClientName={clientName}
      crmActions={crmActions}
      toggle={
        <ActionsToggle variant={variant} className={buttonClassName} aria-label={ariaLabel}>
          <i className='bi bi-three-dots fs-6' />
          {withText ? 'Actions' : null}
        </ActionsToggle>
      }
    >
      <ActionsDropdownItem icon='bi-envelope' label='Send email' to={`/email/new?contactId=${result.clientId}`} />
      <ActionsDropdownItem icon='bi-file-earmark-spreadsheet' label='Export as CSV' onClick={exportCSV} disabled={exportDisabled} />
    </CrossClientActionsDropdown>
  )
}
